import { ComponentPropsWithoutRef, useRef } from "react";
import * as styles from "./HeroSecondary.css";
import { Heading } from "../elements/Heading";
import PortableText, { IPortableText } from "../portableText/PortableText";
import { IImage, Image } from "../elements/Image";
import { ArrowShape } from "~/assets/svgs/ArrowShape";
import { ButtonLink } from "../elements/ButtonLink";
// @ts-ignore
import ArrowOutward from "@material-design-icons/svg/filled/arrow_outward.svg?react";
// @ts-ignore
import Phone from "@material-design-icons/svg/outlined/phone.svg?react";
import { Breadcrumbs, BreadcrumbsProps } from "../elements/Breadcrumbs";
import { HeroSecondary as THeroSecondary } from "~/sanity/types-mapped";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

export type HeroSecondaryProps = ComponentPropsWithoutRef<"div"> & {
  data: THeroSecondary;
  breadcrumbs?: BreadcrumbsProps;
};

export const HeroSecondary = ({ data, breadcrumbs }: HeroSecondaryProps) => {
  const { title, image } = data;

  const heroRef = useRef(null);
  const heroImageRef = useRef(null);
  const contentRef = useRef(null);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger, SplitText);

    const headingEl = document.getElementById("hero-secondary-heading");
    let headingSplit: ReturnType<typeof SplitText.create> | undefined;

    if (headingEl) {
      headingSplit = SplitText.create(headingEl, {
        type: "lines",
        mask: "lines",
      });
      const lines = headingSplit.lines;
      gsap.set(lines, { y: "100%" });
      gsap.set(headingEl, { visibility: "visible" });

      const tlLoad = gsap.timeline();
      const contentEl = contentRef.current;

      tlLoad
        .to(contentEl, {
          paddingTop: "0vh",
          duration: 1,
          ease: "power2.out",
          delay: 0.5,
        })
        .fromTo(
          lines,
          { y: "100%" },
          {
            y: 0,
            stagger: {
              amount: 0.2,
              ease: "power1.out",
            },
            delay: 0.3,
            onComplete: () => {
              headingSplit?.revert();
              headingSplit = undefined;
              gsap.set(headingEl, { overflow: "visible" });
            },
          },
          "<",
        );
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.current,
        start: "center center",
        end: "bottom top",
        scrub: true,
      },
    });

    tl.to(heroImageRef.current, {
      yPercent: 10,
    });

    return () => {
      headingSplit?.revert();
    };
  }, []);

  return (
    <header className={styles.hero} ref={heroRef}>
      <div className={styles.wrapper}>
        <div className={styles.content} ref={contentRef}>
          <Heading
            as="h1"
            size="jumbo"
            uppercase
            id="hero-secondary-heading"
            className={styles.headingHidden}
          >
            {title}
          </Heading>
        </div>
        <div className={styles.imageContainer}>
          <div className={styles.triangle}></div>
          <div className={styles.imageWrapper} ref={heroImageRef}>
            <Image
              image={image as IImage}
              className={styles.image}
              width={1920}
              loading="eager"
            />
          </div>
        </div>
      </div>
    </header>
  );
};
